import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUniversalSearch } from '../universal-search/useUniversalSearch';
import { SelectedDependencyWithClearHistoryProps } from '../dependencies/types';
import { ConfigurationType, DependenciesContentViewType } from '../dependencies/types';
import {
  clearDependencies,
  addConfigurationItems,
  setDependenciesConfigurationItem,
  selectParsedConfigurationItems,
} from '../../../reducers/documentation/documentationReducer';
import { selectSaltoObjectTypes } from '../../../reducers/global/parserReducer';
import { useLayouts } from '../hooks/useLayouts';
import { DocumentationPageType } from '../types';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { UI_EVENTS } from '../../../services/events';
import { telemetry } from '../../../telemetry';
import { ConfigurationItem } from '../dependencies/DependenciesTypes';
import { NameProperties } from '../../parser/ParserTypes';
import {
  setActivePageTab,
  setDocumentationPageType,
} from '../../../reducers/documentation/documentationPageReducer';
import { useUniversalSearchContext } from '../universal-search/UniversalSearchContext';
import useParser from '../../parser/useParser';
import { SearchType } from '../universal-search/types';
import { selectParsedObjects } from '../../../reducers/global/parserReducer';

interface UseObjectListUniversalSearchProps {
  crmOrgId: string;
}

export const useObjectListUniversalSearch = ({ crmOrgId }: UseObjectListUniversalSearchProps) => {
  const dispatch = useDispatch();
  const sendBiEvent = useSendBiEvent();
  const { fetchLayoutData } = useLayouts();
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const parsedObjects = useSelector(selectParsedObjects(crmOrgId));
  const parsedConfigurationItems = useSelector(selectParsedConfigurationItems(crmOrgId));
  const [isLoadingDependency, setIsLoadingDependency] = useState(false);

  const { parseObjectOnDemand } = useParser({ crmOrgId: crmOrgId ?? '' });
  const {
    setSearchTxt,
    setResults,
    setFilterKey,
    setIsUniversalSearchListOpen,
    setSelectedSearchType,
  } = useUniversalSearchContext();

  const handleCloseUniversalSearch = useCallback(() => {
    setSearchTxt('');
    setResults(undefined);
    setFilterKey(undefined);
    setSelectedSearchType(SearchType.apiNameAndLabel);
    setIsUniversalSearchListOpen(false);
    dispatch(setActivePageTab({ tabId: '' }));
    // Switch back to metadata view
    dispatch(
      setDocumentationPageType({ documentationPageType: DocumentationPageType.metadataTypes }),
    );
  }, [
    dispatch,
    setFilterKey,
    setIsUniversalSearchListOpen,
    setResults,
    setSearchTxt,
    setSelectedSearchType,
  ]);

  // Universal search callbacks
  const handleClearDependencies = useCallback(() => {
    dispatch(clearDependencies());
  }, [dispatch]);

  const handleBiEventSearchStarted = useCallback(
    (props: { searchQuery: string; searchType: string }) => {
      sendBiEvent({
        name: UI_EVENTS.documentationSearchStarted,
        props,
      });
    },
    [sendBiEvent],
  );

  const handleBiEventSearchResultsPresented = useCallback(
    (props: { searchQuery: string; numberOfResults: number }) => {
      sendBiEvent({
        name: UI_EVENTS.documentationSearchResultsPresented,
        props,
      });
    },
    [sendBiEvent],
  );

  const handleItemClick = useCallback(
    async (props: SelectedDependencyWithClearHistoryProps, searchResults?: any) => {
      const { parentType, dependencyType, clearHistory, contentType, name, metadataId } = props;
      let { id, objectName } = props;
      let item: ConfigurationItem | NameProperties | undefined;

      const configurationItems = searchResults?.[parentType as keyof typeof searchResults];

      switch (parentType) {
        case ConfigurationType.layouts:
          const isLayoutObjectParsed = parsedObjects?.find((_item) => _item.name === objectName); //if true layout is part of parsed objects already
          const itemInConfigurationItemsIndex = parsedConfigurationItems?.findIndex(
            (_item) => _item.metadataId === metadataId,
          );

          if (itemInConfigurationItemsIndex && itemInConfigurationItemsIndex !== -1) {
            item = parsedConfigurationItems?.[itemInConfigurationItemsIndex];
            break;
          }

          if (!isLayoutObjectParsed && metadataId) {
            //get layout data in case its parent object was not parsed yet
            setIsLoadingDependency(true);
            item = await fetchLayoutData(metadataId, crmOrgId);
            setIsLoadingDependency(false);
          }
          break;

        case ConfigurationType.fields:
          item = configurationItems?.find(
            (_item: any) => _item.name === name && _item.objectName === objectName,
          );
          break;

        case ConfigurationType.objects:
          item = configurationItems?.find((_item: any) => _item.name === name);

          if (contentType !== DependenciesContentViewType.sourceCode) {
            const _objectTypeName = saltoObjectTypes?.find((type) => type.objectType === name);

            if (_objectTypeName) {
              id = _objectTypeName?.objectType;
              objectName = _objectTypeName?.objectType;
              item = { ...item, id, objectName } as ConfigurationItem;

              setIsLoadingDependency(true);
              await parseObjectOnDemand(_objectTypeName.objectType);
              setIsLoadingDependency(false);
            } else {
              telemetry.captureError(new Error(`Couldn't find objectName for:`), { name });
            }
          }
          break;

        default:
          const itemById = configurationItems?.find((_item: any) => _item.id === id);
          const itemByNameAndType = configurationItems?.find(
            (_item: any) => _item.name === name && _item.parentType === parentType,
          ); //for unclassified and files
          item = itemById ?? itemByNameAndType;
          break;
      }

      if (!item) {
        telemetry.captureError(
          `Dependency item not found id: ${id}, parentType: ${parentType}, dependencyType: ${dependencyType} in object: ${objectName}`,
        );
        return;
      }

      //if user wants to drill inside search results the item needs to be added to configuration items first
      dispatch(
        addConfigurationItems({
          crmOrgId,
          newConfigurationItems: { [parentType]: [item] } as any,
        }),
      );

      dispatch(
        setDependenciesConfigurationItem({
          id,
          parentType: parentType as ConfigurationType,
          dependencyType: dependencyType ?? '',
          name,
          objectName,
          clearHistory,
          contentType,
        }),
      );
    },
    [
      dispatch,
      crmOrgId,
      saltoObjectTypes,
      parsedObjects,
      parsedConfigurationItems,
      fetchLayoutData,
      parseObjectOnDemand,
    ],
  );

  const universalSearchResult = useUniversalSearch({
    crmOrgId,
    onItemClick: handleItemClick,
    onClearClick: handleClearDependencies,
    onBiEventSearchStarted: handleBiEventSearchStarted,
    onBiEventSearchResultsPresented: handleBiEventSearchResultsPresented,
  });

  const onSearchKeydownWithEscHandler = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        handleCloseUniversalSearch();
      }
      universalSearchResult.onSearchKeydown(event);
    },
    [handleCloseUniversalSearch, universalSearchResult],
  );

  const openFullModeUniversalSearch = useCallback(() => {
    dispatch(
      setDocumentationPageType({
        documentationPageType: DocumentationPageType.universalSearch,
      }),
    );
  }, [dispatch]);

  return {
    ...universalSearchResult,
    isLoadingDependency,
    handleCloseUniversalSearch,
    onSearchKeydownWithEscHandler,
    openFullModeUniversalSearch,
  };
};
